<template>
  <template v-if="triggerType === 'input'">
    <div class="main-input flex grow flex-col gap-1">
      <!-- only when there is a label, else the gap-1 pushes the field 4px below inputs without a label -->
      <div v-if="label || optional" class="flex items-center gap-1">
        <label v-if="label" class="flex gap-1 text-left"
          >{{ label }} <span v-if="isRequired" class="text-fgr-critical">*</span></label
        >
        <p v-if="optional" class="text-body-default text-levels-fg-03 box-border">{{ `(${t("optional")})` }}</p>
      </div>

      <button
        ref="trigger"
        :disabled="readonly"
        v-bind="$attrs"
        type="button"
        :data-testid="`${testId || name || 'dropdown'}-trigger`"
        class="text-body-default old-input-wrapper bg-input-bg-default hover:bg-input-bg-hover focus-within:bg-input-bg-active! text-input-fgr-filled hover:text-input-fgr-hover focus-within:text-input-fgr-active! focus-within:border-state-focus-border focus-within:outline-input-bdr-active border-input-bdr-default focus-within:border-input-bdr-active hover:border-input-bdr-hover flex h-fit grow items-center gap-1.5 rounded-lg border px-2.5 py-0.75 -outline-offset-1 transition-all duration-300 focus-within:outline"
        :class="{
          'bg-input-bg-critical! text-input-fgr-critical! border-input-fgr-critical! border': error,
          'bg-input-bg-disabled! hover:bg-input-bg-disabled! border-input-bg-disabled! hover:border-input-bg-disabled! text-input-fgr-disabled! hover:text-input-fgr-disabled!':
            readonly,
        }"
        @click="toggleDropdown"
        @keyup.tab.exact.stop.prevent="toggleAndSetFocus()"
      >
        <slot name="prefix" />
        <slot />
        <span :class="['truncate', { 'input-fgr-read-only cursor-inherit': !prettyModelValue }]">{{
          prettyModelValue || placeholder || $t("select_option")
        }}</span>
        <Icon src="CaretUpDown" :size="ComponentSize.sm" class="ms-auto" />
      </button>
    </div>
    <Message v-if="error" variant="invalid">{{ error }}</Message>
    <Message v-if="message" :variant="ButtonVariant.Default">{{ message }}</Message>
  </template>
  <Button
    v-else-if="triggerType === 'button'"
    ref="trigger"
    class="cursor-pointer [&_img]:cursor-pointer"
    :variant="buttonVariant"
    v-bind="$attrs"
    :test-id="`${testId || name || 'dropdown'}-trigger`"
    :aria-label="prettyModelValue ? prettyModelValue : placeholder || $t('select_option')"
    @click.exact.stop.prevent="toggleDropdown"
    @keyup.tab.exact.stop.prevent="toggleAndSetFocus()"
  >
    <div class="flex items-center gap-2">
      <span class="text-body-secondary">{{ placeholder || $t("select_option") }}</span>
      <Icon src="CaretDown" :size="ComponentSize.sm" />
    </div>
  </Button>
  <button
    v-if="triggerType === 'custom'"
    ref="trigger"
    v-bind="$attrs"
    @click.exact.stop.prevent="toggleDropdown"
    @keyup.tab.exact.stop.prevent="toggleAndSetFocus()"
  >
    <slot name="trigger" />
  </button>

  <DropdownMenu
    ref="dropdown"
    v-model:open="showDropdown"
    :max-width="maxWidth"
    :min-width="triggerWidth"
    :calculate-position="calculatePosition"
    :overlay="overlay"
    :content-padding="false"
    :toggle-element="trigger"
  >
    <slot name="extra-options" />
    <template v-if="filterable">
      <DropdownItemSearch
        ref="searchElm"
        v-model="search"
        @keydown.down.prevent="() => setFocusOnItem(-1, 'down')"
        @keydown.up.prevent="() => setFocusOnItem(-1, 'up')"
        @keydown.enter.prevent="selectCreatedOption"
      />
      <Divider :spacing="false" />
    </template>
    <div
      ref="optionsWrapper"
      class="max-h-64 w-full overflow-x-hidden overflow-y-auto p-1.5"
      :class="{
        'pt-1.5': filterable,
      }"
      :style="`min-width:${triggerWidth}; max-width:${itemMaxWidth}`"
    >
      <slot name="options">
        <template v-for="(option, index) in filteredOptions" :key="`${search}_${option ? option.value : 'seperator'}`">
          <DropdownItem
            v-if="option && !option.isHeading"
            class="overflow-hidden text-nowrap text-ellipsis"
            :close-on-click="!multiple"
            :aria-label="option.label"
            :selected="isSelected(option)"
            :data-testid="`${testId || name || 'dropdown'}-option-${option.value}`"
            :disabled="option.disabled"
            @click="() => selectOption(option.value)"
            @keydown.enter.prevent.stop="() => selectOption(option.value)"
            @keydown.down.prevent="() => setFocusOnItem(index, 'down')"
            @keydown.up.prevent="() => setFocusOnItem(index, 'up')"
          >
            <div class="flex items-center gap-1">
              <img v-if="option.image" :src="option.image" loading="lazy" width="20" height="20" />
              <Flag v-else-if="option.flag" :country-code="option.flag" />
              <Avatar v-else-if="option.avatar" :entity="option.avatar" :size="ComponentSize.sm" random-color />
              <Icon v-if="option.icon" :src="option.icon" />
              <div class="flex-col gap-2">
                <div class="overflow-hidden text-ellipsis">{{ option.label }}</div>
                <div v-if="option.subtext" class="text-body-small text-wrap">
                  {{ option.subtext }}
                </div>
              </div>
            </div>
          </DropdownItem>
          <p
            v-else-if="option && option.isHeading"
            class="text-body-small text-fgr-secondary pointer-events-none cursor-default px-0.5 py-0.5"
          >
            {{ option.label }}
          </p>
          <Divider v-else />
        </template>

        <DropdownItem v-if="filteredOptions.length === 0" disabled>
          {{ emptyText || $t("no_options_available") }}
        </DropdownItem>
      </slot>
    </div>
  </DropdownMenu>
</template>

<script lang="ts" setup>
  const {
    buttonVariant = ButtonVariant.Secondary,
    centerHorizontally = false,
    creatable = false,
    emptyText = undefined,
    error = undefined,
    filterable = false,
    itemMaxWidth = "auto",
    label = undefined,
    maxWidth = undefined,
    message = undefined,
    multiple = false,
    name = "",
    options = [],
    overlay = false,
    placeholder = undefined,
    preSelected = false,
    readonly = false,
    testId = undefined,
    triggerType = "input",
    isRequired = false,
    optional = false,
  } = defineProps<{
    buttonVariant?: ButtonVariant;
    centerHorizontally?: boolean;
    creatable?: boolean | ((typed: string) => DropdownOption | null);
    emptyText?: string;
    error?: string;
    filterable?: boolean;
    itemMaxWidth?: string;
    label?: string;
    maxWidth?: string;
    message?: string;
    multiple?: boolean;
    name?: string;
    options?: DropdownOption[];
    overlay?: boolean;
    placeholder?: string;
    preSelected?: boolean;
    readonly?: boolean;
    testId?: string;
    triggerType?: "input" | "button" | "custom";
    isRequired?: boolean;
    optional?: boolean;
  }>();

  const { showDropdown, toggleDropdown, calculatePosition, triggerWidth, dropdown, trigger } = useDropdown({
    offset: { top: 8, left: 0 },
    centerHorizontally,
  });

  const { t } = useI18n();

  const emit = defineEmits(["select"]);
  const modelValue = defineModel<string | number | unknown[]>();

  // #region value
  onMounted(() => {
    if (preSelected && options?.length > 0) {
      selectOption(options[0].value);
      toggleDropdown();
    }
  });

  const isSelected = (option) => {
    if (multiple) {
      return modelValue.value?.includes(option.value);
    }
    return modelValue.value === option.value;
  };

  const {
    searchElm,
    optionsWrapper,
    search,
    filteredOptions,
    setFocusOnItem,
    selectOption,
    selectCreatedOption,
    prettyModelValue,
  } = useSelect({
    options: toRef(() => options),
    modelValue,
    emit,
    open: showDropdown,
    toggleDropdown,
    multiple: multiple,
    creatable,
  });

  const { value: selectedValue } = useField(name, undefined, {
    syncVModel: name ? false : true,
    controlled: !!name,
  });

  watchEffect(() => {
    if (name) {
      if (selectedValue.value && !modelValue.value) {
        modelValue.value = selectedValue.value;
      }
      selectedValue.value = modelValue.value;
    }
  });

  const toggleAndSetFocus = () => {
    if (readonly) return;
    toggleDropdown();
    setTimeout(() => {
      setFocusOnItem(-1, "down");
    }, 300);
  };
  // #endregion

  defineExpose({
    trigger,
    dropdown,
    toggleDropdown,
  });
</script>
